import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MapPin, Store, Truck } from 'lucide-react';
import { useToast } from '@/shared/contexts';
import { OrderInput, OrderItemsTable } from '@/pages/Home/comparison/components';
import { buildOrderDetailLine } from '@/pages/Home/comparison/orderDetail.util';
import type {
  CreateOrderResponse,
  ItemVendorMappingDto,
  OrderItem,
  ParseResponse,
  Vendor
} from '@/pages/Home/comparison/types';
import { Alert } from '@/shared/components/ui/Alert';
import { Button } from '@/shared/components/ui/Button';
import {
  OrderCreationCodeDialog,
  type OrderSessionPayload
} from '@/pages/SuperAdmin/OrderCreationCodeDialog';

const API_URL = import.meta.env.VITE_API_URL || 'https://dev.api.gioallday.com';

function extractApiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  const pick = (value: unknown): string | null => {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ');
    }
    if (typeof value === 'object' && value !== null && 'message' in value) {
      return pick((value as { message: unknown }).message);
    }
    return null;
  };
  return pick(data) || fallback;
}

type PanelStep = 'compose' | 'results';

type RestaurantLocationOption = {
  id: string;
  name?: string | null;
  formatted_address?: string | null;
  is_default?: boolean;
};

/** Matches supplierRestaurantList payload (includes locations). */
type SupplierRestaurantOption = {
  id: string;
  business_name: string;
  locations?: RestaurantLocationOption[];
};

type OrderPageLocationState = {
  orderSession?: OrderSessionPayload;
};

/**
 * Super Admin–only order compose panel.
 * Expects an unlocked order session from the code dialog (nav state),
 * or prompts via dialog if opened directly.
 */
export const SuperAdminSupplierOrderPage: React.FC = () => {
  const { supplierId = '' } = useParams<{ supplierId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useToast();

  /** Under Suppliers section vs standalone Orders nav. */
  const underSuppliers = location.pathname.startsWith(
    '/super-admin/suppliers/'
  );
  const backToList = underSuppliers
    ? '/super-admin/suppliers'
    : '/super-admin/orders';
  const backToSupplier = underSuppliers
    ? `/super-admin/suppliers/${supplierId}`
    : '/super-admin/orders';
  const backLabel = underSuppliers ? 'Back to supplier' : 'All suppliers';

  const navSession = (location.state as OrderPageLocationState | null)
    ?.orderSession;

  const [supplierToken, setSupplierToken] = useState<string | null>(
    navSession?.accessToken ?? null
  );
  const [supplierName, setSupplierName] = useState(
    navSession?.supplier.businessName ||
      navSession?.supplier.email ||
      'Supplier'
  );
  const [supplierUserId, setSupplierUserId] = useState(
    navSession?.supplier.id || supplierId
  );
  const [bootError, setBootError] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState(Boolean(navSession?.accessToken));
  const [codeDialogOpen, setCodeDialogOpen] = useState(
    !navSession?.accessToken
  );

  const [restaurants, setRestaurants] = useState<SupplierRestaurantOption[]>(
    []
  );
  const [selectedRestaurant, setSelectedRestaurant] =
    useState<SupplierRestaurantOption | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [orderText, setOrderText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<PanelStep>('compose');
  const [parseResult, setParseResult] = useState<ParseResponse | null>(null);

  const [editableItems, setEditableItems] = useState<OrderItem[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<Record<number, string>>(
    {}
  );
  const [itemVendorAssignments, setItemVendorAssignments] = useState<
    Record<number, string>
  >({});
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const locationOptions = selectedRestaurant?.locations ?? [];

  const pickDefaultLocation = (restaurant: SupplierRestaurantOption) => {
    const locs = restaurant.locations ?? [];
    if (!locs.length) return '';
    return locs.find(l => l.is_default)?.id || locs[0].id;
  };

  const applySession = (session: OrderSessionPayload) => {
    setSupplierToken(session.accessToken);
    setSupplierUserId(session.supplier.id);
    setSupplierName(
      session.supplier.businessName || session.supplier.email || 'Supplier'
    );
    setUnlocked(true);
    setCodeDialogOpen(false);
    setBootError(null);
  };

  useEffect(() => {
    if (navSession?.accessToken) {
      applySession(navSession);
      // Drop one-time session from history so refresh re-prompts.
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    setSupplierToken(null);
    setSupplierUserId(supplierId);
    setSupplierName('Supplier');
    setUnlocked(false);
    setCodeDialogOpen(true);
    setBootError(null);
    setRestaurants([]);
    setSelectedRestaurant(null);
    setSelectedLocationId('');
    setVendors([]);
    setOrderText('');
    setParseError(null);
    setStep('compose');
    setParseResult(null);
    setEditableItems([]);
    setSelectedUnits({});
    setItemVendorAssignments({});
    setCreateError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when supplier route changes
  }, [supplierId]);

  useEffect(() => {
    if (!supplierToken || !supplierUserId) return;
    let cancelled = false;

    (async () => {
      try {
        const restResp = await axios.get<{ data: SupplierRestaurantOption[] }>(
          `${API_URL}/api/users/supplierRestaurantList`,
          { headers: { Authorization: `Bearer ${supplierToken}` } }
        );
        if (cancelled) return;
        const list = restResp.data.data || [];
        setRestaurants(list);
        if (list.length === 1) {
          setSelectedRestaurant(list[0]);
          setSelectedLocationId(pickDefaultLocation(list[0]));
        }

        try {
          const vendorResp = await axios.get<{ data: Vendor[] }>(
            `${API_URL}/api/users/vendorList?supplier_id=${supplierUserId}`,
            { headers: { Authorization: `Bearer ${supplierToken}` } }
          );
          if (!cancelled) setVendors(vendorResp.data.data || []);
        } catch {
          /* vendors optional */
        }
      } catch {
        if (!cancelled) {
          setBootError('Failed to load restaurants for this supplier');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [supplierToken, supplierUserId]);

  const handleCodeDialogClose = () => {
    setCodeDialogOpen(false);
    navigate(underSuppliers ? backToSupplier : backToList, { replace: true });
  };

  const resetResultsState = () => {
    setParseResult(null);
    setEditableItems([]);
    setSelectedUnits({});
    setItemVendorAssignments({});
    setCreateError(null);
  };

  const handleParse = async () => {
    const trimmed = orderText.trim();
    if (!trimmed) {
      setParseError('Please enter some order text first');
      return;
    }
    if (!selectedRestaurant) {
      setParseError('Please select a restaurant');
      return;
    }
    if (!selectedLocationId) {
      setParseError('Please select a location');
      return;
    }
    if (!supplierToken) return;

    setLoading(true);
    setParseError(null);
    try {
      // Existing API requires restaurantId + locationId as query params for supplier JWT
      const params = new URLSearchParams({
        supplierId: supplierUserId,
        restaurantId: selectedRestaurant.id,
        locationId: selectedLocationId
      });
      const response = await axios.post<ParseResponse>(
        `${API_URL}/api/orders/parse-order?${params.toString()}`,
        {
          text: trimmed,
          supplierId: supplierUserId,
          locationId: selectedLocationId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supplierToken}`
          }
        }
      );
      setParseResult(response.data);
      setEditableItems(response.data.items || []);
      setSelectedUnits({});
      setItemVendorAssignments({});
      setCreateError(null);
      setStep('results');
    } catch (err: unknown) {
      const message = axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      setParseError(message || 'Failed to parse order');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setOrderText('');
    setParseError(null);
    resetResultsState();
    setStep('compose');
  };

  const handleUpdateItem = (
    index: number,
    field: keyof OrderItem,
    value: string | number
  ) => {
    setEditableItems(prev =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveItem = (index: number) => {
    setEditableItems(prev => prev.filter((_, i) => i !== index));
    setItemVendorAssignments(prev => {
      const next: Record<number, string> = {};
      Object.entries(prev).forEach(([key, vendorId]) => {
        const i = Number(key);
        if (i < index) next[i] = vendorId;
        else if (i > index) next[i - 1] = vendorId;
      });
      return next;
    });
    setSelectedUnits(prev => {
      const next: Record<number, string> = {};
      Object.entries(prev).forEach(([key, unit]) => {
        const i = Number(key);
        if (i < index) next[i] = unit;
        else if (i > index) next[i - 1] = unit;
      });
      return next;
    });
  };

  const handleAddItem = () => {
    setEditableItems(prev => [
      ...prev,
      {
        item: '',
        quantity: 1,
        unit: null,
        note: '',
        warning: null,
        is_new: true
      }
    ]);
  };

  const handleUpdateUnit = (index: number, unit: string) => {
    setSelectedUnits(prev => ({ ...prev, [index]: unit }));
  };

  const handleAssignVendor = (index: number, vendor: Vendor) => {
    setItemVendorAssignments(prev => ({ ...prev, [index]: vendor.id }));
  };

  const handleCreateOrder = async () => {
    if (!selectedRestaurant) {
      setCreateError('No restaurant selected');
      return;
    }
    if (!selectedLocationId) {
      setCreateError('No location selected');
      return;
    }
    if (!supplierToken) return;
    if (!editableItems.length) {
      setCreateError('Add at least one item before creating the order');
      return;
    }

    setCreatingOrder(true);
    setCreateError(null);

    try {
      const orderDetails = editableItems.map((item, index) =>
        buildOrderDetailLine(item, selectedUnits[index] ?? item.unit ?? 'unit')
      );

      const itemVendorMappings: ItemVendorMappingDto[] = [];
      editableItems.forEach((item, index) => {
        const assignedVendorId = itemVendorAssignments[index];
        if (assignedVendorId) {
          itemVendorMappings.push({
            item_id: item.item,
            vendor_id: assignedVendorId
          });
        } else if (item.vendor_id) {
          itemVendorMappings.push({
            item_id: item.item,
            vendor_id: item.vendor_id
          });
        }
      });

      await axios.post<CreateOrderResponse>(
        `${API_URL}/api/orders/create?supplierId=${supplierUserId}`,
        {
          new_invoice: true,
          order_number: '',
          restaurant_id: selectedRestaurant.id,
          supplierId: supplierUserId,
          order_name: parseResult?.order_name_suggestion || 'New Order',
          delivery_date: new Date(
            Date.now() + 24 * 60 * 60 * 1000
          ).toISOString(),
          total_quantity: editableItems.reduce(
            (sum, item) => sum + Number(item.quantity || 0),
            0
          ),
          estimated_total: 0,
          order_status: 'pending',
          location: '',
          location_id: selectedLocationId,
          delivery_instructions:
            parseResult?.delivery_instructions_suggestion || '',
          original_order_text: orderText.trim() || undefined,
          order_details: orderDetails,
          item_vendor_mappings: itemVendorMappings
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supplierToken}`
          }
        }
      );

      notify('success', 'Order created successfully!');
      handleClear();
    } catch (err: unknown) {
      setCreateError(extractApiError(err, 'Failed to create order'));
    } finally {
      setCreatingOrder(false);
    }
  };

  if (!unlocked) {
    return (
      <OrderCreationCodeDialog
        open={codeDialogOpen}
        supplierId={supplierId}
        onClose={handleCodeDialogClose}
        onSuccess={session => {
          applySession(session);
          navigate(location.pathname, {
            replace: true,
            state: { orderSession: session }
          });
        }}
      />
    );
  }

  if (bootError) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">{bootError}</Alert>
        <Link
          to={backToSupplier}
          className="inline-flex text-sm font-medium text-[#006B82] hover:underline"
        >
          {backLabel}
        </Link>
      </div>
    );
  }

  if (!supplierToken) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#006B82]" />
      </div>
    );
  }

  const locationLabel =
    locationOptions.find(l => l.id === selectedLocationId)?.name ||
    locationOptions.find(l => l.id === selectedLocationId)?.formatted_address ||
    null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
            {supplierName}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">
            Compose an order for this supplier’s restaurants
          </p>
        </div>
        <Link
          to={underSuppliers ? backToSupplier : backToList}
          className="rounded-lg border border-border-primary bg-background-card px-3 py-2 text-sm font-medium text-text-secondary hover:bg-background-hover"
        >
          {backLabel}
        </Link>
      </div>

      {step === 'compose' && (
        <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm space-y-4">
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-text-secondary">
              <Store className="h-4 w-4 text-text-muted" />
              Restaurant <span className="text-rose-500">*</span>
            </label>
            <select
              value={selectedRestaurant?.id || ''}
              onChange={e => {
                const restaurant =
                  restaurants.find(r => r.id === e.target.value) || null;
                setSelectedRestaurant(restaurant);
                setSelectedLocationId(
                  restaurant ? pickDefaultLocation(restaurant) : ''
                );
              }}
              className="w-full rounded-xl border border-border-primary bg-background-card px-4 py-3 text-sm focus:border-[#006B82] focus:outline-none focus:ring-2 focus:ring-[#006B82]/20"
            >
              <option value="">-- Select a restaurant --</option>
              {restaurants.map(r => (
                <option key={r.id} value={r.id}>
                  {r.business_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-text-secondary">
              <MapPin className="h-4 w-4 text-text-muted" />
              Location <span className="text-rose-500">*</span>
            </label>
            <select
              value={selectedLocationId}
              disabled={!selectedRestaurant}
              onChange={e => setSelectedLocationId(e.target.value)}
              className="w-full rounded-xl border border-border-primary bg-background-card px-4 py-3 text-sm focus:border-[#006B82] focus:outline-none focus:ring-2 focus:ring-[#006B82]/20 disabled:bg-background-secondary"
            >
              <option value="">-- Select a location --</option>
              {locationOptions.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.name || loc.formatted_address || loc.id.slice(0, 8)}
                  {loc.is_default ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </div>

          <OrderInput
            orderText={orderText}
            onOrderTextChange={setOrderText}
            onParse={handleParse}
            onClear={handleClear}
            loading={loading}
            error={parseError}
          />
        </div>
      )}

      {step === 'results' && parseResult && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border-primary bg-background-card px-5 py-4 shadow-sm">
            <div>
              <p className="font-semibold text-text-primary">Review & create</p>
              <p className="text-sm text-text-tertiary">
                {selectedRestaurant?.business_name}
                {locationLabel ? ` · ${locationLabel}` : ''} ·{' '}
                {editableItems.length} item
                {editableItems.length === 1 ? '' : 's'}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleClear}
              disabled={creatingOrder}
            >
              New order
            </Button>
          </div>

          <div className="overflow-hidden rounded-2xl border border-border-primary bg-background-card shadow-sm">
            <OrderItemsTable
              items={editableItems}
              vendors={vendors}
              selectedUnits={selectedUnits}
              itemVendorAssignments={itemVendorAssignments}
              onUpdateItem={handleUpdateItem}
              onRemoveItem={handleRemoveItem}
              onAddItem={handleAddItem}
              onUpdateUnit={handleUpdateUnit}
              onAssignVendor={handleAssignVendor}
              isSupplier={true}
            />

            {parseResult.delivery_instructions_suggestion && (
              <div className="border-t border-border-secondary bg-background-secondary/40 p-6">
                <h3 className="mb-3 flex items-center gap-2 font-semibold text-text-primary">
                  <Truck className="h-4 w-4" />
                  Delivery Instructions
                </h3>
                <p className="whitespace-pre-wrap rounded-xl border border-border-secondary bg-background-card p-4 text-sm leading-relaxed text-text-secondary">
                  {parseResult.delivery_instructions_suggestion}
                </p>
              </div>
            )}

            {createError && (
              <div className="border-t border-border-secondary px-6 pt-4">
                <Alert variant="destructive">{createError}</Alert>
              </div>
            )}

            <div className="flex justify-end gap-3 border-t border-border-secondary bg-background-card p-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetResultsState();
                  setStep('compose');
                }}
                disabled={creatingOrder}
              >
                Back to edit
              </Button>
              <Button
                type="button"
                onClick={handleCreateOrder}
                disabled={creatingOrder || editableItems.length === 0}
                className="bg-[#006B82] hover:bg-[#005A6E]"
              >
                {creatingOrder && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {creatingOrder ? 'Creating...' : 'Create Order'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
